import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000',
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // 30 seconds
});

// Request interceptor: attach auth token or fallback to API key
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('freight_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else if (import.meta.env.VITE_API_KEY) {
      config.headers['X-API-Key'] = import.meta.env.VITE_API_KEY;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response.data; // Return just the data
  },
  (error) => {
    // On 401, clear auth state and redirect to login
    // (but not if the failing request was the login endpoint itself)
    if (
      error.response?.status === 401 &&
      !error.config?.url?.includes('/api/freight/auth/login')
    ) {
      localStorage.removeItem('freight_token');
      window.location.href = '/admin/login';
      return Promise.reject(error);
    }

    // Handle other errors
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
    console.error('API Error:', errorMessage);
    return Promise.reject(error);
  }
);

/**
 * Provider API
 */
export const providerAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    return api.get(`/api/freight/providers?${params.toString()}`);
  },

  getById: (id) => api.get(`/api/freight/providers/${id}`),

  create: (data) => api.post('/api/freight/providers', data),

  update: (id, data) => api.patch(`/api/freight/providers/${id}`, data),

  delete: (id) => api.delete(`/api/freight/providers/${id}`),

  getStats: (id) => api.get(`/api/freight/providers/${id}/stats`),

  findMatching: (criteria) => api.post('/api/freight/providers/match', criteria)
};

/**
 * Comparison API
 */
export const comparisonAPI = {
  compare: (shipmentId, criteria = null) =>
    api.post(`/api/freight/shipments/${shipmentId}/quotes/compare`, { criteria }),

  get: (comparisonId) =>
    api.get(`/api/freight/comparisons/${comparisonId}`),

  select: (shipmentId, quoteId, data) =>
    api.post(`/api/freight/shipments/${shipmentId}/quotes/${quoteId}/select`, data)
};

/**
 * Quote API
 */
export const quoteAPI = {
  get: (quoteId) =>
    api.get(`/api/freight/quotes/${quoteId}`),

  confirm: (quoteId, method = 'EMAIL') =>
    api.post(`/api/freight/quotes/${quoteId}/confirm`, { confirmationMethod: method }),

  getEvidence: (quoteId) =>
    api.get(`/api/freight/quotes/${quoteId}/evidence`)
};

/**
 * Shipment API
 */
export const shipmentAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    return api.get(`/api/freight/shipments?${params.toString()}`);
  },

  getById: (id) => api.get(`/api/freight/shipments/${id}`),

  create: (data) => api.post('/api/freight/shipments', data),

  update: (id, data) => api.patch(`/api/freight/shipments/${id}`, data),

  requestQuotes: (id) => api.post(`/api/freight/shipments/${id}/quotes/request`),

  getQuotes: (id) => api.get(`/api/freight/shipments/${id}/quotes`)
};

/**
 * Task API
 */
export const taskAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    return api.get(`/api/freight/tasks?${params.toString()}`);
  },

  getById: (id) => api.get(`/api/freight/tasks/${id}`),

  update: (id, data) => api.patch(`/api/freight/tasks/${id}`, data),

  assign: (id, assignedTo) => api.post(`/api/freight/tasks/${id}/assign`, { assignedTo }),

  resolve: (id, resolvedBy, resolutionNotes) =>
    api.post(`/api/freight/tasks/${id}/resolve`, { resolvedBy, resolutionNotes })
};

/**
 * Analytics API
 */
export const analyticsAPI = {
  getPipeline: () => api.get('/api/freight/analytics/pipeline'),

  getProviderPerformance: (limit = 10) =>
    api.get(`/api/freight/analytics/providers?limit=${limit}`),

  getSavings: () => api.get('/api/freight/analytics/savings'),

  getStaleProviders: (days = 90) =>
    api.get(`/api/freight/analytics/stale-providers?days=${days}`),

  getRedFlags: () => api.get('/api/freight/analytics/red-flags'),

  getTaskSummary: () => api.get('/api/freight/analytics/tasks'),

  // Stage 6: Advanced Analytics
  getProviderScorecard: (id) =>
    api.get(`/api/freight/analytics/providers/${id}/scorecard`),

  getProviderTrends: (id, months = 6) =>
    api.get(`/api/freight/analytics/providers/${id}/trends?months=${months}`),

  getLanes: () => api.get('/api/freight/analytics/lanes'),

  getLanePricing: (origin, destination, months = 6) =>
    api.get(`/api/freight/analytics/lanes/${origin}-${destination}/pricing?months=${months}`),

  getQuoteMetrics: (from, to) =>
    api.get(`/api/freight/analytics/quote-metrics?from=${from || ''}&to=${to || ''}`),

  getQuoteTurnaround: (from, to) =>
    api.get(`/api/freight/analytics/quote-turnaround?from=${from || ''}&to=${to || ''}`),

  getSavingsDetail: (from, to) =>
    api.get(`/api/freight/analytics/savings/detail?from=${from || ''}&to=${to || ''}`),

  getEstimateVsActual: (from, to) =>
    api.get(`/api/freight/analytics/estimate-vs-actual?from=${from || ''}&to=${to || ''}`),

  getDeliveryPerformance: (from, to) =>
    api.get(`/api/freight/analytics/delivery-performance?from=${from || ''}&to=${to || ''}`),

  recalculateScores: () =>
    api.post('/api/freight/analytics/recalculate-scores'),

  exportCsv: (reportType, from, to) =>
    api.get(`/api/freight/analytics/export/${reportType}?from=${from || ''}&to=${to || ''}`, {
      responseType: 'blob'
    })
};

/**
 * Quote Explorer API — composite filter analytics
 */
export const quoteExplorerAPI = {
  query: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, value);
    });
    return api.get(`/api/freight/analytics/quote-explorer?${params.toString()}`);
  },

  getOptions: () =>
    api.get('/api/freight/analytics/quote-explorer/options'),

  exportCsv: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, value);
    });
    return api.get(`/api/freight/analytics/quote-explorer/export?${params.toString()}`, {
      responseType: 'blob'
    });
  }
};

/**
 * Estimate API — freight cost estimates
 */
export const estimateAPI = {
  generateEstimate: (data) =>
    api.post('/api/freight/estimates', data),

  getEstimate: (shipmentId) =>
    api.get(`/api/freight/estimates/${shipmentId}`),

  getEstimateHistory: (shipmentId) =>
    api.get(`/api/freight/estimates/${shipmentId}/history`),

  getAccuracyStats: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) query.append(key, value);
    });
    return api.get(`/api/freight/estimates/accuracy?${query.toString()}`);
  },

  getTruckingParameters: () =>
    api.get('/api/freight/estimates/trucking-parameters'),

  updateTruckingParameter: (id, data) =>
    api.put(`/api/freight/estimates/trucking-parameters/${id}`, data),

  getDutyRates: () =>
    api.get('/api/freight/estimates/duty-rates'),

  updateDutyRate: (id, data) =>
    api.put(`/api/freight/estimates/duty-rates/${id}`, data),

  // V2: Calculate full landed cost with customs breakdown
  calculate: (shipmentId, params = {}) =>
    api.get(`/api/freight/estimates/${shipmentId}/calculate`, { params }),

  // V2: Finalize estimate with optional admin overrides
  finalize: (shipmentId, data) =>
    api.post(`/api/freight/estimates/${shipmentId}/finalize`, data),

  // V3: Standalone estimate (no shipmentId required)
  calculateStandalone: (data) =>
    api.post('/api/freight/estimates/calculate', data),

  // V3: Multi-shipment batch estimate
  calculateBatch: (shipments) =>
    api.post('/api/freight/estimates/calculate-batch', { shipments }),

  // V3: Estimate config (multiplier, etc.)
  getConfig: () =>
    api.get('/api/freight/estimates/config'),

  updateConfig: (key, value) =>
    api.put(`/api/freight/estimates/config/${key}`, { value })
};

/**
 * Forwarder Rate Cards API — ocean freight rate cards by forwarder
 */
export const forwarderRateAPI = {
  getAll: () => api.get('/api/freight/estimates/forwarder-rates'),

  create: (data) => api.post('/api/freight/estimates/forwarder-rates', data),

  update: (id, data) => api.put(`/api/freight/estimates/forwarder-rates/${id}`, data)
};

/**
 * Customer Drayage Rates API — drayage rates per customer/route
 */
export const customerDrayageAPI = {
  getAll: () => api.get('/api/freight/estimates/customer-drayage'),

  create: (data) => api.post('/api/freight/estimates/customer-drayage', data),

  update: (id, data) => api.put(`/api/freight/estimates/customer-drayage/${id}`, data)
};

/**
 * US Customs Rates API — duty, Section 301, additional tariff rates by HS code
 */
export const usCustomsRateAPI = {
  getAll: () => api.get('/api/freight/estimates/us-customs-rates'),

  create: (data) => api.post('/api/freight/estimates/us-customs-rates', data),

  update: (id, data) => api.put(`/api/freight/estimates/us-customs-rates/${id}`, data)
};

/**
 * Customs Broker Fees API — broker fee schedules per forwarder
 */
export const customsBrokerFeeAPI = {
  getAll: () => api.get('/api/freight/estimates/customs-broker-fees'),

  create: (data) => api.post('/api/freight/estimates/customs-broker-fees', data),

  update: (id, data) => api.put(`/api/freight/estimates/customs-broker-fees/${id}`, data)
};

/**
 * Tracking API
 */
export const trackingAPI = {
  getTracking: (shipmentId) =>
    api.get(`/api/freight/tracking/${shipmentId}`),

  addEvent: (shipmentId, data) =>
    api.post(`/api/freight/tracking/${shipmentId}/events`, data),

  getMilestones: (shipmentId) =>
    api.get(`/api/freight/tracking/${shipmentId}/milestones`),

  getETA: (shipmentId) =>
    api.get(`/api/freight/tracking/${shipmentId}/eta`),

  getDelays: (shipmentId) =>
    api.get(`/api/freight/tracking/${shipmentId}/delays`)
};

/**
 * Conversation API
 */
export const conversationAPI = {
  getForQuoteRequest: (quoteRequestId) =>
    api.get(`/api/freight/conversations/${quoteRequestId}`),

  getEventsForOrder: (orderId) =>
    api.get(`/api/freight/conversations/events/${orderId}`)
};

/**
 * Health check API
 */
export const healthAPI = {
  check: () => api.get('/health')
};

/**
 * Auth API — login, password reset, user profile
 */
export const authAPI = {
  login: (email, password) =>
    api.post('/api/freight/auth/login', { email, password }),

  forgotPassword: (email) =>
    api.post('/api/freight/auth/forgot-password', { email }),

  resetPassword: (token, password) =>
    api.post('/api/freight/auth/reset-password', { token, password }),

  getMe: () =>
    api.get('/api/freight/auth/me'),

  changePassword: (currentPassword, newPassword) =>
    api.put('/api/freight/auth/change-password', { currentPassword, newPassword })
};

/**
 * Manufacturer Settings API — per-factory quote trigger configuration
 */
export const manufacturerSettingsAPI = {
  getSettings: (manufacturerId) =>
    api.get(`/api/freight/manufacturers/${manufacturerId}/quote-settings`),

  updateSettings: (manufacturerId, data) =>
    api.put(`/api/freight/manufacturers/${manufacturerId}/quote-settings`, data),

  bulkGetSettings: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) query.append(key, value);
    });
    return api.get(`/api/freight/manufacturers/quote-settings/bulk?${query.toString()}`);
  }
};

/**
 * HS Code API — lookup and management
 */
export const hsCodeAPI = {
  lookup: (params) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) query.append(key, value);
    });
    return api.get(`/api/freight/hs-codes/lookup?${query.toString()}`);
  },

  getForManufacturer: (manufacturerId) =>
    api.get(`/api/freight/manufacturers/${manufacturerId}/hs-codes`),

  create: (manufacturerId, data) =>
    api.post(`/api/freight/manufacturers/${manufacturerId}/hs-codes`, data),

  delete: (manufacturerId, hsCodeId) =>
    api.delete(`/api/freight/manufacturers/${manufacturerId}/hs-codes/${hsCodeId}`)
};

/**
 * Fuel Cost API — price management
 */
export const fuelCostAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) query.append(key, value);
    });
    return api.get(`/api/freight/fuel-costs?${query.toString()}`);
  },

  getCurrent: (region = 'US') =>
    api.get(`/api/freight/fuel-costs/current?region=${region}`),

  getAllCurrent: () =>
    api.get('/api/freight/fuel-costs/all'),

  update: (data) =>
    api.post('/api/freight/fuel-costs', data),

  fetchEIA: () =>
    api.post('/api/freight/fuel-costs/fetch-eia')
};

/**
 * Write-back API — audit and retry
 */
export const writebackAPI = {
  getStatus: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) query.append(key, value);
    });
    return api.get(`/api/freight/writeback-status?${query.toString()}`);
  },

  retry: (logId) =>
    api.post(`/api/freight/writeback/${logId}/retry`)
};

/**
 * Platform Admin API — tenant and prompt management (V1 admin proxy)
 */
export const platformAdminAPI = {
  getStats: () => api.get('/api/freight/admin/stats'),
  listTenants: () => api.get('/api/freight/admin/tenants'),
  getTenant: (id) => api.get(`/api/freight/admin/tenants/${id}`),
  updateTenantStatus: (id, status) => api.patch(`/api/freight/admin/tenants/${id}/status`, { status }),
  updateTenantPlan: (id, planData) => api.patch(`/api/freight/admin/tenants/${id}/plan`, planData),
  listTenantUsers: (id) => api.get(`/api/freight/admin/tenants/${id}/users`),
  listTenantAudit: (id, params = {}) => api.get(`/api/freight/admin/tenants/${id}/audit`, { params }),
  updateUserStatus: (userId, status) => api.patch(`/api/freight/admin/users/${userId}/status`, { status }),
  // Prompts
  listPrompts: () => api.get('/api/freight/admin/prompts'),
  getPrompt: (taskType) => api.get(`/api/freight/admin/prompts/${taskType}`),
  updatePromptConfig: (taskType, data) => api.put(`/api/freight/admin/prompts/${taskType}/config`, data),
  listPromptVersions: (taskType) => api.get(`/api/freight/admin/prompts/${taskType}/versions`),
  createPromptVersion: (taskType, data) => api.post(`/api/freight/admin/prompts/${taskType}/versions`, data),
  setActivePromptVersion: (taskType, promptVersionId) => api.put(`/api/freight/admin/prompts/${taskType}/active-version`, { promptVersionId }),
  // Port catalogue
  listPorts: (params = {}) => api.get('/api/freight/admin/ports', { params }),
  createPort: (data) => api.post('/api/freight/admin/ports', data),
  updatePort: (locode, portType, data) => api.patch(`/api/freight/admin/ports/${locode}/${portType}`, data),
  deactivatePort: (locode, portType) => api.delete(`/api/freight/admin/ports/${locode}/${portType}`),
  // AI Observability
  listLlmInteractions: (params = {}) => api.get('/api/freight/admin/llm-interactions', { params }),
  getLlmInteraction: (id) => api.get(`/api/freight/admin/llm-interactions/${id}`),
  getLlmInteractionsByChatSession: (sessionId) => api.get(`/api/freight/admin/llm-interactions/by-chat-session/${sessionId}`)
};

export default api;
